import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  LinearProgress,
  Link,
  Stack,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import api from '../services/api';
import { useSnackbar } from '../components/AppSnackbar';
import type { ImportPreviewResult, ImportResult, ImportError } from '@shared/types/import-export.types';

// ============================================================
// Constants
// ============================================================

const STEPS = ['ファイル選択', 'プレビュー', '結果'];

const PREVIEW_COLUMNS: { key: string; label: string }[] = [
  { key: 'assetNumber', label: '資産番号' },
  { key: 'category', label: '資産区分' },
  { key: 'manufacturer', label: 'メーカー' },
  { key: 'modelNumber', label: '型番' },
  { key: 'status', label: '状態' },
];

// ============================================================
// Component
// ============================================================

const ImportPage: React.FC = () => {
  const navigate = useNavigate();
  const { showError } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState<ImportPreviewResult | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // ----------------------------------------------------------
  // File selection handlers
  // ----------------------------------------------------------

  const handleFileSelect = useCallback((file: File) => {
    if (!file.name.endsWith('.csv')) {
      showError('CSVファイルを選択してください');
      return;
    }
    setSelectedFile(file);
  }, [showError]);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setIsDragOver(false);
      const file = event.dataTransfer.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // ----------------------------------------------------------
  // Preview step
  // ----------------------------------------------------------

  const handlePreview = useCallback(async () => {
    if (!selectedFile) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.post<ImportPreviewResult>('/assets/import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreviewResult(response.data);
      setActiveStep(1);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      showError(err.response?.data?.message || 'プレビューの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [selectedFile, showError]);

  // ----------------------------------------------------------
  // Import execution
  // ----------------------------------------------------------

  const handleImport = useCallback(async () => {
    if (!selectedFile) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.post<ImportResult>('/assets/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(response.data);
      setActiveStep(2);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      showError(err.response?.data?.message || 'インポートに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [selectedFile, showError]);

  // ----------------------------------------------------------
  // Navigation
  // ----------------------------------------------------------

  const handleCancel = useCallback(() => {
    setActiveStep(0);
    setSelectedFile(null);
    setPreviewResult(null);
    setImportResult(null);
  }, []);

  const handleBackToList = useCallback(() => {
    navigate('/assets');
  }, [navigate]);

  const handleTemplateDownload = useCallback(() => {
    window.open('/api/assets/export/csv?template=true', '_blank');
  }, []);

  // ----------------------------------------------------------
  // Render steps
  // ----------------------------------------------------------

  const renderFileSelectionStep = () => (
    <Box>
      <Paper
        data-testid="drop-zone"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          p: 4,
          textAlign: 'center',
          border: '2px dashed',
          borderColor: isDragOver ? 'primary.main' : 'grey.400',
          backgroundColor: isDragOver ? 'action.hover' : 'background.paper',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          },
        }}
        onClick={handleBrowseClick}
      >
        <CloudUploadIcon sx={{ fontSize: 48, color: 'grey.500', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          CSVファイルをドラッグ&ドロップ
        </Typography>
        <Typography variant="body2" color="text.secondary">
          または、クリックしてファイルを選択
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          対応文字コード: UTF-8
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          hidden
          onChange={handleInputChange}
          data-testid="file-input"
        />
      </Paper>

      {selectedFile && (
        <Alert severity="info" sx={{ mt: 2 }}>
          選択中: {selectedFile.name}
        </Alert>
      )}

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 3 }}>
        <Link
          component="button"
          variant="body2"
          onClick={handleTemplateDownload}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          <DownloadIcon fontSize="small" />
          テンプレートCSVをダウンロード
        </Link>

        <Button
          variant="contained"
          onClick={handlePreview}
          disabled={!selectedFile || loading}
        >
          次へ
        </Button>
      </Stack>
    </Box>
  );

  const renderPreviewStep = () => {
    if (!previewResult) return null;

    return (
      <Box>
        <Stack spacing={2}>
          {/* Summary */}
          <Alert severity="info">
            インポート対象: {previewResult.totalRows}件（有効: {previewResult.validRows}件、エラー: {previewResult.errorRows}件）
          </Alert>

          {/* Preview table */}
          {previewResult.preview.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                プレビュー（先頭5件）
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {PREVIEW_COLUMNS.map((col) => (
                        <TableCell key={col.key}>{col.label}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewResult.preview.map((row, index) => (
                      <TableRow key={index}>
                        {PREVIEW_COLUMNS.map((col) => (
                          <TableCell key={col.key}>
                            {(row as Record<string, unknown>)[col.key] as string || ''}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Error list */}
          {previewResult.errors.length > 0 && (
            <Box>
              <Typography variant="subtitle2" color="error" gutterBottom>
                バリデーションエラー一覧
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>行番号</TableCell>
                      <TableCell>フィールド</TableCell>
                      <TableCell>エラー内容</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewResult.errors.map((err: ImportError, index: number) => (
                      <TableRow key={index}>
                        <TableCell>{err.row}</TableCell>
                        <TableCell>{err.field}</TableCell>
                        <TableCell>{err.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Actions */}
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button variant="outlined" onClick={handleCancel} disabled={loading}>
              キャンセル
            </Button>
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={loading || previewResult.validRows === 0}
            >
              インポート実行
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  };

  const renderResultStep = () => {
    if (!importResult) return null;

    const hasErrors = importResult.errorCount > 0;

    return (
      <Box>
        <Stack spacing={3} alignItems="center">
          {/* Result icon */}
          {hasErrors ? (
            <ErrorIcon sx={{ fontSize: 64, color: 'warning.main' }} />
          ) : (
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />
          )}

          {/* Summary */}
          <Typography variant="h6">
            インポート完了
          </Typography>

          <Stack spacing={1} alignItems="center">
            <Typography>
              成功: {importResult.successCount}件
            </Typography>
            <Typography color={hasErrors ? 'error' : 'text.secondary'}>
              失敗: {importResult.errorCount}件
            </Typography>
          </Stack>

          {/* Error details */}
          {importResult.errors.length > 0 && (
            <Box sx={{ width: '100%' }}>
              <Typography variant="subtitle2" color="error" gutterBottom>
                エラー詳細
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>行番号</TableCell>
                      <TableCell>フィールド</TableCell>
                      <TableCell>エラー内容</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importResult.errors.map((err: ImportError, index: number) => (
                      <TableRow key={index}>
                        <TableCell>{err.row}</TableCell>
                        <TableCell>{err.field}</TableCell>
                        <TableCell>{err.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Actions */}
          <Button variant="contained" onClick={handleBackToList}>
            資産台帳一覧へ
          </Button>
        </Stack>
      </Box>
    );
  };

  // ----------------------------------------------------------
  // Main render
  // ----------------------------------------------------------

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 48px - 48px)', overflow: 'hidden' }}>
      {/* Fixed header */}
      <Box sx={{ flexShrink: 0, pb: 1 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
          CSVインポート
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Scrollable content */}
      <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'auto' }}>
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {activeStep === 0 && renderFileSelectionStep()}
        {activeStep === 1 && renderPreviewStep()}
        {activeStep === 2 && renderResultStep()}
      </Box>
    </Box>
  );
};

export default ImportPage;
